import { ConfigurablePropsFromDescription } from '../../../helpers/widget'
import PublishUtils from '../../../helpers/PublishUtils'
import { useRequiredContext } from '../../../utils/use-required-context'
import { createContext } from 'react'
import { IconHelp } from '../IconHelp'

export const configOptions = [
  {
    name: 'query',
    type: 'yasgui',
    label: '?uri ?label ?color ?parentUri ?icon ?iconColor ?iconSize ?tooltip ?defaultChecked(xsd:boolean) ?rightLabel ?rightLabelColor ?rightIcon ?rightIconColor ?rightIconSize',
    helpComponent: TreePlusPlusHelp
  },
  {
    name: 'selectMode',
    type: 'select',
    label: 'Tree selection mode',
    options: [
      { label: 'Disabled', value: 'disabled' },
      { label: 'Single', value: 'single' },
      { label: 'Multiple (use Ctrl and Shift)', value: 'multiple' }
    ],
  },
  {
    name: 'checkboxesEnabled',
    type: 'boolean',
    label: 'Enable checkboxes (this is separate from selection)',
  },
  {
    name: 'checkboxLayout',
    type: 'select',
    label: 'Checkboxes presentation (aligned or nested)',
    options: [
      { label: 'Aligned', value: 'aligned' },
      { label: 'Nested', value: 'nested' },
    ],
  },
  {
    name: 'lastSelectedItemTopic',
    type: 'text',
    label: 'Topic to publish last selected item to'
  },
  {
    name: 'selectedItemsTopic',
    type: 'text',
    label: 'Topic to publish selected items to'
  },
  {
    name: 'checkedItemsTopic',
    type: 'text',
    label: 'Topic to publish checked items to'
  },
  {
    name: 'checkedItemsPublishMode',
    type: 'select',
    label: 'How to publish checked items',
    options: [
      { label: 'All nodes', value: 'all' },
      { label: 'Leaf nodes only', value: 'leaves' },
    ],
  },
  {
    name: 'selectedItemSubscription',
    type: 'text',
    label: 'Listen topic for item selection'
  },
  {
    name: 'selectedItemsSubscription',
    type: 'text',
    label: 'Listen topic for multi item selection. This is based on your multiple value configuration.'
  },
  {
    name: 'checkedItemsSubscription',
    type: 'text',
    label: 'Listen topic for checking items. This is based on your multiple value configuration.'
  },
  {
    name: 'selectedItemRevealMode',
    type: 'select',
    label: 'Behaviour for automatically revealing selected item. Only applies to the single item selection topic.',
    defaultValue: 'disabled',
    options: [
      { label: 'Do not reveal', value: 'disabled' },
      { label: 'Reveal instantly', value: 'instant' },
      { label: 'Reveal smoothly', value: 'smooth' },
    ],
  },
  PublishUtils.getMultipleValueFormatOptions()
] as const

export type TreePlusPlusConfigOptions = ConfigurablePropsFromDescription<typeof configOptions>

export const TreeConfigContext = createContext<TreePlusPlusConfigOptions | null>(null)

export const useTreeConfig = () => useRequiredContext(TreeConfigContext)

function TreePlusPlusHelp() {
  type HelpParam = { name: string, type: string, description?: string }
  const PARAMS: HelpParam[] = [
    { name: '?uri', type: 'URI or literal' },
    { name: '?parentUri', type: 'URI or literal' },
    { name: '?label', type: 'literal' },
    { name: '?color', type: 'literal', description: 'css color' },
    { name: '?icon', type: 'literal' },
    { name: '?iconColor', type: 'literal', description: 'css color' },
    { name: '?iconSize', type: 'literal', description: 'css size unit' },
    { name: '?defaultChecked', type: 'xsd:boolean literal' },
    { name: '?rightLabel', type: 'literal', description: 'optional label at the right size' },
    { name: '?rightLabelColor', type: 'literal', description: 'css color (default is grey)' },
    { name: '?rightIcon', type: 'literal', description: 'default is radix-dot' },
    { name: '?rightIconColor', type: 'literal', description: 'css color' },
    { name: '?rightIconSize', type: 'literal', description: 'css size unit' },
  ] as const
  return (
    <div>
      <table>
        <tbody>
        {PARAMS.map((param, i) => (
          <tr>
            <td>{param.name}</td>
            <td>{param.type}</td>
            <td>{param.description ?? ''}</td>
          </tr>
        ))}
        </tbody>
      </table>

      <IconHelp className="mt-3" />
    </div>

  )
}
